import { useState, useRef, useCallback, useEffect } from 'react';
import { useGameStore } from '../../store/gameStore';
import { CHARACTERS } from '../../data';
import type { FateCard } from '../../types';
import styles from './FatePreview.module.css';

interface Props {
  isPortrait?: boolean;
}

export default function FatePreview({ isPortrait }: Props) {
  const players = useGameStore(s => s.players);
  const currentPlayerIndex = useGameStore(s => s.currentPlayerIndex);
  const getFateDeckPreview = useGameStore(s => s.getFateDeckPreview);
  const phase = useGameStore(s => s.phase);
  const [collapsed, setCollapsed] = useState(false);
  const [expandedCardIdx, setExpandedCardIdx] = useState<number | null>(null);

  // 拖拽状态
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({
    startX: 0,
    startY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
    moved: false,      // 是否真的移动了（>3px）
  });

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // 不拦截按钮点击（收起按钮等）
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button')) return;
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startOffsetX: dragOffset.x,
      startOffsetY: dragOffset.y,
      moved: false,
    };
    setIsDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [dragOffset.x, dragOffset.y]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      dragRef.current.moved = true;
    }
    if (dragRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
    }
    setDragOffset({
      x: dragRef.current.startOffsetX + dx,
      y: dragRef.current.startOffsetY + dy,
    });
  }, [isDragging]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 全局 pointerup 兜底（防止拖到元素外松开鼠标不触发事件）
  useEffect(() => {
    const onGlobalUp = () => setIsDragging(false);
    window.addEventListener('pointerup', onGlobalUp);
    return () => window.removeEventListener('pointerup', onGlobalUp);
  }, []);

  // 收起/横竖屏切换时：重置拖拽位置 + moved 标记
  useEffect(() => {
    setDragOffset({ x: 0, y: 0 });
    dragRef.current.moved = false;
  }, [collapsed, isPortrait]);

  const cp = players[currentPlayerIndex];
  if (!cp || cp.characterId !== 'eagle' || cp.isBot) return null;
  if (phase !== 'playing') return null;

  const previewCards: FateCard[] = getFateDeckPreview();
  if (previewCards.length === 0) return null;

  const char = CHARACTERS.find(c => c.id === 'eagle');
  const categoryLabel = (cat: string) => {
    switch (cat) {
      case 'good': return '好运';
      case 'bad': return '厄运';
      case 'reverse': return '反转';
      default: return cat;
    }
  };
  const categoryClass = (cat: string) => {
    switch (cat) {
      case 'good': return styles.categoryGood;
      case 'bad': return styles.categoryBad;
      case 'reverse': return styles.categoryReverse;
      default: return '';
    }
  };

  // 解析效果描述文本
  const getEffectSummary = (card: FateCard): string => {
    const desc = card.description;
    // 提取金额/效果关键词
    if (desc.includes('获得') || desc.includes('收入') || desc.includes('金币')) return '💰 资金变动';
    if (desc.includes('失去') || desc.includes('罚款') || desc.includes('支付')) return '💸 资金损失';
    if (desc.includes('移动') || desc.includes('前进') || desc.includes('后退')) return '🚶 位置移动';
    if (desc.includes('监狱') || desc.includes('入狱') || desc.includes('出狱')) return '🔒 监狱相关';
    if (desc.includes('反转卡') || desc.includes('获得一张')) return '🃏 卡牌获取';
    if (desc.includes('地产') || desc.includes('房屋') || desc.includes('升级')) return '🏠 地产相关';
    if (desc.includes('跳过') || desc.includes('休息') || desc.includes('回合')) return '⏭️ 回合影响';
    return '📋 查看详情';
  };

  const dragStyle: React.CSSProperties = {
    transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
    transition: isDragging ? 'none' : 'transform 0.15s ease-out',
  };

  if (collapsed) {
    return (
      <div
        className={styles.collapsedBtn}
        style={dragStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        title="长按拖拽移动"
      >
        <button
          className={styles.collapsedInner}
          onClick={(e) => {
            // 只有没拖拽过才触发展开
            if (!dragRef.current.moved) {
              e.stopPropagation();
              setCollapsed(false);
            }
          }}
          title="展开预知未来"
        >
          <span className={styles.collapsedIcon}>{char?.emoji || '🦅'}</span>
          <span className={styles.collapsedBadge}>{previewCards.length}</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className={`${styles.panel} ${isDragging ? styles.dragging : ''}`}
      style={{ ...dragStyle, pointerEvents: 'auto' }}
    >
      <div
        className={styles.header}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <span className={styles.dragHandle} title="拖拽移动">⋮⋮</span>
        <span className={styles.icon}>{char?.emoji || '🦅'}</span>
        <span className={styles.title}>预知未来</span>
        <button
          className={styles.collapseBtn}
          onClick={() => setCollapsed(true)}
          title="收起"
        >
          −
        </button>
      </div>
      <div className={styles.cards}>
        {previewCards.map((card, i) => {
          const isExpanded = expandedCardIdx === i;
          return (
            <div key={i}>
              <div
                className={`${styles.cardItem} ${isExpanded ? styles.cardItemExpanded : ''}`}
                onClick={() => setExpandedCardIdx(isExpanded ? null : i)}
              >
                <span className={styles.cardOrder}>#{i + 1}</span>
                <span className={styles.cardEmoji}>{card.emoji}</span>
                <span className={styles.cardName}>{card.name}</span>
                <span className={`${styles.cardCat} ${categoryClass(card.category)}`}>
                  {categoryLabel(card.category)}
                </span>
                <span className={styles.cardExpandHint}>{isExpanded ? '▲' : '▼'}</span>
              </div>
              {isExpanded && (
                <div className={styles.cardDetail}>
                  <p className={styles.cardDetailDesc}>{card.description}</p>
                  <span className={styles.cardDetailTag}>{getEffectSummary(card)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
